import { useEffect, useMemo, useRef, useState } from "react";
import { apiFetch } from "../lib/api";
import Hls from "hls.js";

type Line = [[number, number], [number, number]];
type LinesV2 = { line1: Line; line2: Line };
type ActiveCam = { id: string };

function useQuery() {
  return useMemo(() => new URLSearchParams(window.location.search), []);
}

function clamp(n: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, n));
}

export default function ThresholdViewWindow() {
  const query = useQuery();
  const cameraFromQuery = query.get("camera") ?? "";

  // Video + overlay
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const hlsRef = useRef<Hls | null>(null);
  const refreshTimer = useRef<number | null>(null);

  // Natural video size
  const [naturalSize, setNaturalSize] = useState({ w: 1920, h: 1080 });
  const naturalLocked = useRef(false);

  // Cameras
  const [cameras, setCameras] = useState<ActiveCam[]>([]);
  const [cameraId, setCameraId] = useState(cameraFromQuery || "");
  const [camsLoading, setCamsLoading] = useState(false);

  // Threshold data
  const [lines, setLines] = useState<LinesV2 | null>(null);
  const [cameraRole, setCameraRole] = useState<string | null>(null);
  const [status, setStatus] = useState("Select a camera to view its threshold.");
  const [ready, setReady] = useState(false);

  // ---------- Fetch cameras ----------
  useEffect(() => {
    let mounted = true;
    async function loadCams() {
      setCamsLoading(true);
      try {
        const res = await apiFetch("/api/cameras/active");
        const data = await res.json();
        if (!res.ok) throw new Error(JSON.stringify(data));
        if (!mounted) return;

        setCameras(Array.isArray(data) ? data : []);
        if (!cameraId && Array.isArray(data) && data.length) {
          setCameraId(data[0].id);
        }
      } catch (e: any) {
        if (mounted) setStatus(`Failed to load cameras: ${e?.message ?? String(e)}`);
      } finally {
        if (mounted) setCamsLoading(false);
      }
    }
    loadCams();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- HLS helpers ----------
  async function fetchHlsUrl(cam: string) {
    const res = await apiFetch(
      `/api/kvs/hls-url?stream=${encodeURIComponent(cam)}&mode=LIVE&max_frag=3`
    );
    if (!res.ok) throw new Error("hls-url fetch failed");
    const { url } = await res.json();
    return url as string;
  }

  function clearRefresh() {
    if (refreshTimer.current) {
      window.clearTimeout(refreshTimer.current);
      refreshTimer.current = null;
    }
  }

  function scheduleRefresh(ms: number) {
    clearRefresh();
    refreshTimer.current = window.setTimeout(() => {
      startHls(true).catch(() => {});
    }, ms) as unknown as number;
  }

  async function startHls(_isRefresh = false) {
    const video = videoRef.current;
    if (!video || !cameraId) return;

    const url = await fetchHlsUrl(cameraId);

    video.muted = true;
    video.playsInline = true;
    (video as any).autoplay = true;

    if (!Hls.isSupported()) {
      return new Promise<void>((resolve) => {
        const onCanPlay = () => {
          setReady(true);
          video.removeEventListener("canplay", onCanPlay);
          resolve();
        };
        video.addEventListener("canplay", onCanPlay, { once: true });
        video.src = url;
        video.play().catch(() => {});
        scheduleRefresh(4 * 60_000);
      });
    }

    let hls = hlsRef.current;
    if (!hls) {
      hls = new Hls({ enableWorker: true, liveBackBufferLength: 30 });
      hlsRef.current = hls;

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        setReady(true);
        video.play().catch(() => {});
      });

      hls.on(Hls.Events.ERROR, (_evt, data) => {
        if (data?.fatal || data?.details === Hls.ErrorDetails.MANIFEST_LOAD_ERROR) {
          startHls(true).catch(() => {});
        }
      });

      hls.attachMedia(video);
    }

    hls.loadSource(url);
    scheduleRefresh(4 * 60_000);
  }

  // Start/restart HLS when camera changes
  useEffect(() => {
    setReady(false);
    startHls().catch((e) => setStatus(`HLS start failed: ${e?.message ?? e}`));
    return () => {
      clearRefresh();
      try {
        hlsRef.current?.destroy();
      } catch {}
      hlsRef.current = null;

      const v = videoRef.current;
      if (v) {
        try {
          v.removeAttribute("src");
          v.load();
        } catch {}
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraId]);

  // ---------- Load threshold config ----------
  function normalizeToFrame(src: any): LinesV2 | null {
    if (!src?.line1 || !src?.line2) return null;
    const y1 = Number(src.line1?.[0]?.[1]);
    const y2 = Number(src.line2?.[0]?.[1]);
    if (!Number.isFinite(y1) || !Number.isFinite(y2)) return null;

    let yy1 = clamp(Math.round(y1), 0, naturalSize.h - 1);
    let yy2 = clamp(Math.round(y2), 0, naturalSize.h - 1);
    if (yy2 <= yy1) yy2 = clamp(yy1 + 5, 0, naturalSize.h - 1);

    return {
      line1: [[0, yy1], [naturalSize.w - 1, yy1]],
      line2: [[0, yy2], [naturalSize.w - 1, yy2]],
    };
  }

  async function loadThreshold(cam: string) {
    if (!cam) return;

    try {
      setStatus(`Loading threshold for ${cam}…`);
      const res = await apiFetch(`/threshold_zones/${encodeURIComponent(cam)}`);
      const data = await res.json();

      if (!res.ok) {
        setLines(null);
        setCameraRole(null);
        setStatus(`No threshold saved for ${cam}.`);
        return;
      }

      // Try V3 format first (with camera_role)
      const version = data?.version;
      const role = data?.camera_role;

      if (version === 3) {
        // V3: trigger/threshold lines
        const trigger = data?.lines?.trigger;
        const threshold = data?.lines?.threshold;

        if (trigger && threshold) {
          const normalized = normalizeToFrame({
            line1: trigger,
            line2: threshold,
          });
          if (normalized) {
            setLines(normalized);
            setCameraRole(role || null);
            setStatus(`Loaded V3 threshold for ${cam} (role: ${role || "unknown"})`);
            return;
          }
        }
      }

      // Try V2 format (lines.active.line1/line2)
      const v2Active = data?.lines?.active;
      if (v2Active?.line1 && v2Active?.line2) {
        const normalized = normalizeToFrame(v2Active);
        if (normalized) {
          setLines(normalized);
          setCameraRole(role || null);
          setStatus(`Loaded V2 threshold for ${cam}${role ? ` (role: ${role})` : ""}`);
          return;
        }
      }

      // Legacy fallback: single line
      if (data?.line) {
        const y = data.line?.[0]?.[1] ?? 0;
        const yy = clamp(Math.round(y), 0, naturalSize.h - 1);
        setLines({
          line1: [[0, yy], [naturalSize.w - 1, yy]],
          line2: [[0, yy], [naturalSize.w - 1, yy]],
        });
        setCameraRole(null);
        setStatus(`Loaded legacy threshold for ${cam}`);
        return;
      }

      setLines(null);
      setCameraRole(null);
      setStatus(`Threshold for ${cam} has unsupported format.`);
    } catch (e: any) {
      setLines(null);
      setCameraRole(null);
      setStatus(`Failed to load threshold: ${e?.message ?? String(e)}`);
    }
  }

  // Auto-load threshold when camera changes
  useEffect(() => {
    if (!cameraId) return;
    loadThreshold(cameraId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraId]);

  // ---------- Canvas sizing/overlay ----------
  function getDisplayBox() {
    const v = videoRef.current;
    if (!v) return null;
    const elRect = v.getBoundingClientRect();
    const elW = elRect.width;
    const elH = elRect.height;
    const nW = naturalSize.w || 1;
    const nH = naturalSize.h || 1;
    const scale = Math.min(elW / nW, elH / nH);
    const dispW = nW * scale;
    const dispH = nH * scale;
    const offX = (elW - dispW) / 2;
    const offY = (elH - dispH) / 2;
    return { elRect, dispW, dispH, offX, offY, scale };
  }

  function syncCanvasToVideo() {
    const v = videoRef.current;
    const c = canvasRef.current;
    if (!v || !c) return;
    const rect = v.getBoundingClientRect();
    c.width = Math.max(1, Math.floor(rect.width));
    c.height = Math.max(1, Math.floor(rect.height));
  }

  function drawOverlay(clearOnly = false) {
    const c = canvasRef.current;
    const v = videoRef.current;
    if (!c || !v) return;
    const ctx = c.getContext("2d");
    if (!ctx) return;

    ctx.clearRect(0, 0, c.width, c.height);
    if (clearOnly || !lines) return;

    const box = getDisplayBox();
    if (!box) return;
    const { offX, offY, scale } = box;

    const drawLine = (y: number, color: string, label: string) => {
      const sy = Math.round(offY + y * scale);
      ctx.strokeStyle = color;
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(Math.round(offX), sy);
      ctx.lineTo(Math.round(offX + (naturalSize.w - 1) * scale), sy);
      ctx.stroke();

      ctx.fillStyle = "rgba(0,0,0,0.7)";
      ctx.fillRect(10, sy - 24, 280, 24);
      ctx.fillStyle = "white";
      ctx.font = "14px monospace";
      ctx.fillText(`${label}  y=${y}`, 16, sy - 6);
    };

    drawLine(lines.line1[0][1], "rgba(0,255,255,0.95)", "Trigger (start collect)");
    drawLine(lines.line2[0][1], "rgba(255,80,80,0.95)", "Threshold (fire event)");
  }

  // Keep canvas sized and redraw on changes
  useEffect(() => {
    const onResize = () => {
      syncCanvasToVideo();
      drawOverlay();
    };
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    drawOverlay();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines, naturalSize, ready]);

  // Lock natural size on metadata
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onMeta = () => {
      if (!naturalLocked.current && v.videoWidth && v.videoHeight) {
        setNaturalSize({ w: v.videoWidth, h: v.videoHeight });
        naturalLocked.current = true;
      }
      syncCanvasToVideo();
      drawOverlay();
    };
    v.addEventListener("loadedmetadata", onMeta);
    return () => v.removeEventListener("loadedmetadata", onMeta);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="w-full h-screen bg-zinc-900 text-zinc-100 flex flex-col">
      {/* Header */}
      <div className="px-4 py-3 border-b border-zinc-800 flex items-center gap-3 flex-wrap">
        <div className="font-semibold text-lg">
          View Threshold — {cameraId || "(no camera selected)"}
        </div>
        <div className="text-sm opacity-80">{status}</div>

        <div className="ml-auto flex items-center gap-2">
          {/* Camera selector */}
          <select
            className="px-3 py-2 rounded bg-zinc-800 border border-zinc-700 text-white"
            value={cameraId}
            disabled={camsLoading}
            onChange={(e) => setCameraId(e.target.value)}
          >
            <option value="" disabled>
              {camsLoading ? "Loading cameras..." : "Select camera"}
            </option>
            {cameras.map((c) => (
              <option key={c.id} value={c.id}>
                {c.id}
              </option>
            ))}
          </select>

          {/* Refresh button */}
          <button
            className="px-3 py-2 rounded bg-zinc-700 hover:bg-zinc-600 disabled:opacity-50"
            onClick={() => loadThreshold(cameraId)}
            disabled={!cameraId}
          >
            Reload
          </button>
        </div>
      </div>

      {/* Camera role indicator */}
      {cameraRole && (
        <div className="px-4 py-2 bg-zinc-800 border-b border-zinc-700 text-sm">
          <span className="text-zinc-400">Camera Role: </span>
          <span
            className={`font-semibold ${
              cameraRole === "entrance" ? "text-blue-400" : "text-green-400"
            }`}
          >
            {cameraRole.toUpperCase()}
          </span>
          <span className="text-zinc-500 ml-4">
            {cameraRole === "entrance"
              ? "Crossings = entries (is_inside=True)"
              : "Crossings = exits (is_inside=False)"}
          </span>
        </div>
      )}

      {/* Video + overlay */}
      <div className="flex-1 grid place-items-center p-4 overflow-hidden">
        <div className="relative max-w-full max-h-full">
          <video
            ref={videoRef}
            className="block max-w-full max-h-[calc(100vh-200px)] object-contain rounded bg-black"
            playsInline
            muted
            autoPlay
            onLoadedMetadata={() => {
              const v = videoRef.current;
              if (v && !naturalLocked.current && v.videoWidth && v.videoHeight) {
                setNaturalSize({ w: v.videoWidth, h: v.videoHeight });
                naturalLocked.current = true;
              }
              syncCanvasToVideo();
              drawOverlay();
            }}
            onCanPlay={() => setReady(true)}
          />
          <canvas ref={canvasRef} className="absolute inset-0 z-10 pointer-events-none" />
          {!ready && (
            <div className="absolute inset-0 grid place-items-center text-gray-400 bg-black/50">
              <div className="text-center">
                <div className="text-xl mb-2">Loading video stream...</div>
                <div className="text-sm opacity-60">Camera: {cameraId}</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}